import {
  AuthObserver,
  circadiaEngineProvider,
  localStorageProvider,
} from "../../../../AppController";
import { normalizeCognitoUsername } from "../../../../util/cognitoUsernameNormalize";
import { parseJwt } from "../../../../util/jwt";

const env = import.meta.env;

/**
 * Native NEW_PASSWORD maps `attrs` to `userAttributes.*`. Login `user.email` is often the
 * Cognito username (E.164 phone); sending that as `email` makes Cognito return
 * InvalidParameterException: Invalid email address format.
 */
function attrsForNativeCognitoNewPassword(additionalProps: {
  email: string;
}): Record<string, string> | undefined {
  const email = additionalProps?.email?.trim() ?? "";
  if (!email.includes("@")) return undefined;
  return { email };
}

export const AuthProviderFactory: IAuthProviderFactory = ({
  Auth,
  CircadiaEngine,
}) => {
  // Do NOT cache this at factory init time — on Android the bridge script is
  // injected asynchronously and may not be ready when AppController runs.
  // Calling isWindowCircadia() per-function ensures we get the correct value
  // after the bridge has had time to inject.
  const isCircadiaEngine = () => CircadiaEngine.isWindowCircadia();

  // ── Native event → AuthObserver wiring ───────────────────────────────────
  // AppController.ts registers these callbacks at module load time, but on
  // Android the bridge arrives AFTER module load so that block is skipped
  // (isWindowCircadia() is false at startup). We re-register here on the first
  // login attempt once we know the bridge is ready. The flag prevents
  // double-registration if this is also called on iOS (where AppController's
  // startup block already ran).
  let _nativeCallbacksEnsured = false;
  const ensureNativeCallbacks = () => {
    if (_nativeCallbacksEnsured || !isCircadiaEngine()) return;
    if (CircadiaEngine.intfv < 5) return;
    _nativeCallbacksEnsured = true;

    CircadiaEngine.setCognitoLoginDoneCb?.(() => {
      AuthObserver.update({ type: "login", payload: { success: true } });
    });
    CircadiaEngine.setCognitoLoginFailedCb?.((error: { Msg?: string }) => {
      AuthObserver.update({
        type: "login",
        payload: { success: false, error: error?.Msg },
      });
    });
    CircadiaEngine.setCognitoMfaRequiredCb?.(
      (props: { CODE_DELIVERY_DESTINATION?: string }) => {
        AuthObserver.update({
          type: "mfaRequired",
          payload: { success: false, props: props?.CODE_DELIVERY_DESTINATION },
        });
      }
    );
    CircadiaEngine.setCognitoPinRequiredCb?.(() => {
      AuthObserver.update({ type: "pinRequired", payload: null });
    });
    CircadiaEngine.setCognitoNewPasswordRequiredCb?.((attrs: unknown) => {
      AuthObserver.update({ type: "newPasswordRequired", payload: attrs });
    });
    CircadiaEngine.setCognitoEmailOTPRequiredCb?.(() => {
      AuthObserver.update({ type: "passwordlessOTPRequired", payload: null });
    });
    CircadiaEngine.setCognitoSmsOTPRequiredCb?.(
      (data: { destination?: string }) => {
        AuthObserver.update({
          type: "smsOTPRequired",
          payload: { success: false, props: data?.destination },
        });
      }
    );
  };

  const ssoLogin = async (authParams: {
    token: string;
    expires_at: number;
  }) => {
    // Use the current user pool from environment variables
    const region = env.VITE_AWS_REGION || "us-east-1";
    const userPoolId = env.VITE_COGNITO_USER_POOL_ID;
    const providerId = `cognito-idp.${region}.amazonaws.com/${userPoolId}`;

    console.log("SSO Login with provider:", providerId);
    const res = await Auth.federatedSignIn(providerId, authParams);
    return res;
  };

  const signIn = async (email: string, password: string) => {
    if (isCircadiaEngine()) {
      ensureNativeCallbacks();
      return new Promise<User>((resolve, reject) => {
        const observerEvent = ({
          type,
          payload,
        }: {
          type: string;
          payload: {
            success: boolean;
            error?: string;
            props?: unknown;
          };
        }) => {
          AuthObserver.unsubscribe(observerEvent);
          switch (type) {
            case "login":
              if (payload.success) {
                resolve({
                  email: email as string,
                  userID: "",
                  role: "",
                  firstName: "",
                  lastName: "",
                });
              } else {
                reject(payload.error);
              }
              break;
            case "mfaRequired":
              console.log("mfaRequired", payload);
              resolve({
                email: email as string,
                userID: "",
                role: "",
                firstName: "",
                lastName: "",
                challengeName: "SMS_MFA",
                challengeParam: {
                  CODE_DELIVERY_DESTINATION: payload.props as string,
                  CODE_DELIVERY_DELIVERY_MEDIUM: "",
                },
              });
              break;
            case "newPasswordRequired":
              console.log("newPasswordRequired", payload);
              resolve({
                email: email as string,
                userID: "",
                role: "",
                firstName: "",
                lastName: "",
                challengeName: "NEW_PASSWORD_REQUIRED",
              });
              break;
            case "passwordlessOTPRequired":
              resolve({
                email: email as string,
                userID: "",
                role: "",
                firstName: "",
                lastName: "",
                challengeName: "CUSTOM_CHALLENGE",
              });
              break;
            case "smsOTPRequired":
              resolve({
                email: email as string,
                userID: "",
                role: "",
                firstName: "",
                lastName: "",
                challengeName: "SMS_OTP",
                challengeParam: {
                  CODE_DELIVERY_DESTINATION: payload.props as string,
                  CODE_DELIVERY_DELIVERY_MEDIUM: "SMS",
                },
              });
              break;
            default:
              console.log("unknown event", type, payload);
              reject(null);
              break;
          }
        };

        AuthObserver.subscribe(observerEvent);
        const cognitoId = normalizeCognitoUsername(email);
        console.log("cognitoId", cognitoId, "password", password);
        if (password === "__PHONE_OTP__") {
          CircadiaEngine.cognitoPhoneOTPLogin(cognitoId);
        } else if (password) {
          CircadiaEngine.cognitoUserLogin(cognitoId, password);
        } else {
          CircadiaEngine.cognitoUserEmailOTPLogin(cognitoId);
        }
      });
    } else {
      // Amplify treats any truthy password as SRP (`signInWithPassword`). Phone OTP
      // must use empty password so `signInWithoutPassword` runs `CUSTOM_AUTH` (same as
      // native `cognitoPhoneOTPLogin` / email OTP with no password).
      const cognitoId = normalizeCognitoUsername(email);
      const passwordForAmplify =
        password === "__PHONE_OTP__" ? "" : password ?? "";
      if (passwordForAmplify === "") {
        // The active "fast" Amplify config (amplifyConfigDataNewSetUp) does not set
        // authenticationFlowType, so Amplify defaults to USER_SRP_AUTH. That makes
        // Auth.signIn with an empty password fail for email/phone OTP because the
        // CUSTOM_AUTH initiate is never sent. Switch the flow type just for this
        // call and restore the SRP default on the way out so password sign-ins are
        // unaffected.
        Auth.configure({ authenticationFlowType: "CUSTOM_AUTH" });
        try {
          const user = await Auth.signIn(cognitoId);
          // Cognito's CUSTOM_AUTH Lambda is a 2-stage state machine. The first
          // InitiateAuth returns CUSTOM_CHALLENGE with `challenge: "INIT"` —
          // it has not yet sent any OTP. The CreateAuthChallenge Lambda only
          // emails / SMSes the code once it sees a follow-up
          // RespondToAuthChallenge with ANSWER=__INIT__ and a clientMetadata
          // hint of which channel to use. The native plugin
          // (NativeAuthPlugin.swift / NativeAuthPlugin.kt) does exactly this
          // automatically — mirror it here so the web behaves identically.
          if (
            user?.challengeName === "CUSTOM_CHALLENGE" &&
            user?.challengeParam?.challenge === "INIT"
          ) {
            const loginMethod =
              password === "__PHONE_OTP__" ? "phone" : "email";
            return await Auth.sendCustomChallengeAnswer(user, "__INIT__", {
              loginMethod,
            });
          }
          return user;
        } finally {
          Auth.configure({ authenticationFlowType: "USER_SRP_AUTH" });
        }
      }
      return Auth.signIn(cognitoId, passwordForAmplify);
    }
  };

  const confirmSignIn = async (
    user: User,
    code: string,
    challengeType:
      | "SMS_MFA"
      | "SOFTWARE_TOKEN_MFA"
      | "CUSTOM_CHALLENGE"
      | "SMS_OTP"
      | null
      | undefined
  ) => {
    if (isCircadiaEngine()) {
      const mfaResponse = await new Promise((resolve, reject) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const observerEvent = ({
          type,
          payload,
        }: {
          type: string;
          payload: { success: boolean; error: string | null };
        }) => {
          AuthObserver.unsubscribe(observerEvent);
          switch (type) {
            case "login":
              if (payload.success) {
                resolve({
                  email: user.email,
                  userID: "",
                  role: "",
                  firstName: "",
                  lastName: "",
                });
              } else {
                reject({ message: payload.error || "" });
              }
              break;
            case "pinRequired":
              resolve({
                email: user.email,
                userID: "",
                role: "",
                firstName: "",
                lastName: "",
                challengeName: "PIN",
              });
              break;
            case "passwordlessOTPRequired":
            case "smsOTPRequired":
            case "mfaRequired":
              // Native re-issued the same challenge instead of completing it —
              // this only happens when the code the user just entered was wrong
              // (Cognito re-prompts rather than failing outright). Without these
              // cases a wrong SMS OTP fell through to `default: reject(null)`,
              // whose null error was silently swallowed upstream, so the user
              // saw no feedback at all on a wrong code.
              reject({ message: "Invalid code" });
              break;
            default:
              console.log("unknown event", type, payload);
              // Never reject with null — an unclassifiable error is swallowed by
              // the OTP screen. Surface a real message so the user gets feedback.
              reject({
                message: (payload && payload.error) || "Verification failed",
              });
              break;
          }
        };
        AuthObserver.subscribe(observerEvent);
        if (challengeType === "SMS_OTP") {
          CircadiaEngine.cognitoProvideSmsOTP(code);
        } else if (challengeType === "CUSTOM_CHALLENGE") {
          CircadiaEngine.cognitoProvideEmailOTP(code);
        } else {
          CircadiaEngine.cognitoProvideMfa(code);
        }
      });
      return mfaResponse as User | null;
    } else {
      if (challengeType === "CUSTOM_CHALLENGE") {
        const res = await Auth.sendCustomChallengeAnswer(user, code);
        if (res?.challengeName === "CUSTOM_CHALLENGE") {
          throw new Error("Invalid code", {
            cause: res,
          });
        }
        return res;
      }
      return Auth.confirmSignIn(user, code, challengeType);
    }
  };

  const signOut = async () => {
    if (isCircadiaEngine()) {
      localStorageProvider.remove("userInfoUpdated");
      await Auth.signOut();
      return circadiaEngineProvider.cognitoUserLogout();
    } else {
      return Auth.signOut();
    }
  };

  const completeNewPassword = async (
    user: User,
    password: string,
    additionalProps: {
      email: string;
    }
  ) => {
    if (isCircadiaEngine()) {
      return new Promise<User>((resolve, reject) => {
        const observerEvent = ({
          type,
          payload,
        }: {
          type: string;
          payload: {
            success: boolean;
            error?: string;
            props?: unknown;
          };
        }) => {
          AuthObserver.unsubscribe(observerEvent);
          switch (type) {
            case "login":
              if (payload.success) {
                resolve({
                  email: user.email,
                  userID: "",
                  role: "",
                  firstName: "",
                  lastName: "",
                });
              } else {
                reject(payload.error);
              }
              break;
            case "mfaRequired":
              console.log("mfaRequired", payload);
              resolve({
                email: user.email,
                userID: "",
                role: "",
                firstName: "",
                lastName: "",
                challengeName: "SMS_MFA",
                challengeParam: {
                  CODE_DELIVERY_DESTINATION: payload.props as string,
                  CODE_DELIVERY_DELIVERY_MEDIUM: "",
                },
              });
              break;
            case "newPasswordRequired":
              // Cognito re-issued the challenge — the submitted password was
              // rejected by policy (e.g. password-history, complexity).
              // Resolve would cause useHook to loop via onLoginSubmit; reject
              // so the error path runs and the user sees a clear message.
              reject({
                __type: "PasswordHistoryPolicyViolationException",
                message:
                  "This password has been used before. Please choose a different password.",
              });
              break;
            default:
              console.log("unknown event", type, payload);
              reject(null);
              break;
          }
        };

        AuthObserver.subscribe(observerEvent);
        CircadiaEngine.cognitoProvideNewPassword(
          password,
          attrsForNativeCognitoNewPassword(additionalProps)
        );
      });
    } else {
      return Auth.completeNewPassword(user, password, additionalProps);
    }
  };

  const forgotPassword = (email: string) => {
    return Auth.forgotPassword(email);
  };

  const resetPassword = (email: string, code: string, password: string) => {
    return Auth.forgotPasswordSubmit(email, code, password);
  };

  return {
    signIn,

    ssoLogin,

    currentSession: () => Auth.currentSession(),

    currentUserInfo: async () => {
      if (localStorageProvider.get("zpax") === "true") {
        try {
          // For SSO and ZPAX, get user info from current session
          const session = await Auth.currentSession();
          console.log("session", session);
          const idToken = session.getIdToken();
          console.log("idToken", idToken);
          const payload = idToken.payload;

          return {
            attributes: {
              "custom:first_name": Array.isArray(payload["custom:first_name"])
                ? payload["custom:first_name"][0]
                : payload["custom:first_name"],
              "custom:last_name": Array.isArray(payload["custom:last_name"])
                ? payload["custom:last_name"][0]
                : payload["custom:last_name"],
              sub: Array.isArray(payload.sub) ? payload.sub[0] : payload.sub,
              email: Array.isArray(payload.email)
                ? payload.email[0]
                : payload.email ||
                  (Array.isArray(payload["custom:pcc_email"])
                    ? payload["custom:pcc_email"][0]
                    : payload["custom:pcc_email"]),
              "custom:home_health_id": Array.isArray(
                payload["custom:home_health_id"]
              )
                ? payload["custom:home_health_id"][0]
                : payload["custom:home_health_id"],
            },
          };
        } catch (error) {
          console.error("Error getting current user info for SSO/ZPAX:", error);
          // Fallback to stored token for ZPAX
          if (localStorageProvider.get("zpax") === "true") {
            const storedToken = localStorageProvider.get("idToken");
            if (storedToken) {
              const user = parseJwt(storedToken);
              return {
                attributes: {
                  "custom:first_name": user["custom:first_name"] || "",
                  "custom:last_name": user["custom:last_name"] || "",
                  sub: user.sub || "",
                  email: user.email || "",
                  "custom:home_health_id": user["custom:home_health_id"] || "",
                },
              };
            }
          }
          throw error;
        }
      } else if (localStorageProvider.get("sso") === "true") {
        const { token } = await Auth.currentAuthenticatedUser();

        if (token) {
          const user = parseJwt(token);
          return {
            attributes: {
              "custom:first_name": user["custom:first_name"],
              "custom:last_name": user["custom:last_name"],
              sub: user.sub,
              email: user["custom:pcc_email"],
              "custom:home_health_id": user["custom:home_health_id"],
              "custom:pcc_org_uuid": user["custom:pcc_org_uuid"],
              "custom:pcc_email": user["custom:pcc_email"],
              phone_number: user["phone_number"],
              "custom:m_credentials": user["custom:m_credentials"],
            },
          };
        }
      } else {
        return Auth.currentUserInfo();
      }
    },

    currentAuthenticatedUser: () => Auth.currentAuthenticatedUser(),

    completeNewPassword,

    confirmSignIn,

    changePassword: async (
      user: User | string,
      oldPassword: string,
      newPassword: string
    ) => {
      if (isCircadiaEngine()) {
        let res = await CircadiaEngine.cognitoChangePassword(
          user as string,
          oldPassword,
          newPassword
        );
        if (res.code === 0) {
          return "SUCCESS";
        }

        throw new Error(res.mdg);
      }
      return Auth.changePassword(user as User, oldPassword, newPassword);
    },

    signOut,

    configure: (config: Record<string, string>) => Auth.configure(config),

    forgotPassword,

    resetPassword,

    // ── Self-signup (native bridge only in v1) ──────────────────────────

    signUp: async (payload: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
      birthdate: string;
      gender?: string;
      phone?: string;
    }) => {
      if (!isCircadiaEngine()) {
        throw new Error("Self-signup requires the native app.");
      }
      return CircadiaEngine.cognitoUserSignUp(
        payload.email,
        payload.password,
        payload.firstName,
        payload.lastName,
        payload.birthdate,
        payload.gender ?? "",
        payload.phone
      );
    },

    confirmSignUp: async (email: string, code: string) => {
      if (!isCircadiaEngine()) {
        throw new Error("Self-signup requires the native app.");
      }
      return CircadiaEngine.cognitoConfirmSignUp(email, code);
    },

    resendSignUpCode: async (email: string) => {
      if (!isCircadiaEngine()) {
        throw new Error("Self-signup requires the native app.");
      }
      return CircadiaEngine.cognitoResendConfirmationCode(email);
    },
  };
};
